import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const FORMATS = [
  { id: "both", label: "PDF + Word" },
  { id: "pdf", label: "Solo PDF" },
  { id: "docx", label: "Solo Word" },
];

const CATEGORY_COLORS = {
  rrhh: "#E5484D",
  comunicaciones: "#FFC53D",
  contratos: "#3E63DD",
  legal: "#7C3AED",
  comercial: "#16A34A",
  finanzas: "#0EA5E9",
  actas: "#F97316",
  cartas: "#1C1917",
};

function NeoInput(props) {
  return (
    <input
      {...props}
      className={`w-full h-11 px-3 border-2 border-[#1C1917] bg-white text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow ${props.className || ""}`}
    />
  );
}

function NeoTextarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full px-3 py-2 border-2 border-[#1C1917] bg-white text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow ${props.className || ""}`}
    />
  );
}

function NeoSelect({ options, ...props }) {
  return (
    <select
      {...props}
      className={`w-full h-11 px-3 border-2 border-[#1C1917] bg-white text-[#1C1917] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow ${props.className || ""}`}
    >
      {options.map((o) => (
        <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
      ))}
    </select>
  );
}

function NeoCard({ children, className = "", ...rest }) {
  return (
    <div
      {...rest}
      className={`border-2 border-[#1C1917] bg-white shadow-[6px_6px_0_0_#1C1917] ${className}`}
    >
      {children}
    </div>
  );
}

function FormatPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="format-picker">
      {FORMATS.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => onChange(f.id)}
          className={`h-11 border-2 border-[#1C1917] text-xs font-black tracking-wider uppercase transition-all ${
            value === f.id
              ? "bg-[#1C1917] text-[#FDFBF7] shadow-[3px_3px_0_0_#FFC53D]"
              : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
          }`}
          data-testid={`format-${f.id}`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

function ResultBox({ result, onSign }) {
  if (!result) return null;
  return (
    <NeoCard className="p-6 mt-6" data-testid="docgen-result">
      <span className="eyebrow text-xs">Documento listo</span>
      <h3 className="display text-2xl mt-2 text-[#1C1917]">{result.title}</h3>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {result.files.map((f) => (
          <div key={f.id} className="border-2 border-[#1C1917] bg-[#FDFBF7] p-4 flex items-center justify-between">
            <div>
              <div className="font-black text-sm text-[#1C1917] truncate max-w-[18ch]" title={f.original_filename}>
                {f.original_filename}
              </div>
              <div className="text-xs text-[#57534E] uppercase">{f.ext} · {Math.round(f.size / 1024)} KB</div>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => downloadFile(f.id, f.original_filename)}
                className="rounded-none h-9 border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] hover:bg-black font-black text-xs"
                data-testid={`btn-download-${f.ext}`}
              >
                Descargar
              </Button>
              {f.ext === "pdf" && (
                <Button
                  onClick={() => onSign(f)}
                  className="rounded-none h-9 border-2 border-[#1C1917] bg-[#E5484D] text-white hover:bg-[#CE2C31] font-black text-xs"
                  data-testid="btn-sign-result"
                >
                  Firmar PDF
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </NeoCard>
  );
}

function VariableField({ v, value, onChange }) {
  const common = {
    name: v.key,
    value: value || "",
    onChange: (e) => onChange(v.key, e.target.value),
    placeholder: v.placeholder || "",
    "data-testid": `var-${v.key}`,
    required: !!v.required,
  };
  if (v.type === "textarea") return <NeoTextarea rows={3} {...common} />;
  if (v.type === "date") return <NeoInput type="date" {...common} />;
  if (v.type === "number") return <NeoInput type="number" {...common} />;
  if (v.type === "select") {
    return (
      <NeoSelect
        {...common}
        options={[{ value: "", label: "— Selecciona —" }, ...(v.options || []).map((o) => ({ value: o, label: o }))]}
      />
    );
  }
  return <NeoInput type="text" {...common} />;
}

function CompanyProfileEditor({ profile, onSaved }) {
  const [form, setForm] = useState(profile || {});
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => setForm(profile || {}), [profile]);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.post("/docgen/profile", {
        name: form.name || null,
        cif: form.cif || null,
        address: form.address || null,
        email: form.email || null,
        phone: form.phone || null,
      });
      toast.success("Datos de empresa guardados");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function uploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/docgen/profile/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved?.({ ...(form || {}), logo_file_id: data.logo_file_id });
      toast.success("Logo cargado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLogoUploading(false);
    }
  }

  async function removeLogo() {
    try {
      await api.delete("/docgen/profile/logo");
      onSaved?.({ ...(form || {}), logo_file_id: null });
    } catch (e) {
      toast.error(e.message);
    }
  }

  return (
    <NeoCard className="p-6" data-testid="company-profile-editor">
      <div className="flex items-center justify-between mb-4">
        <span className="eyebrow text-xs">Datos de tu empresa (membrete)</span>
        <span className="text-[10px] text-[#57534E] uppercase tracking-wider">Se usan en cada documento generado</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="eyebrow text-xs">Razón social</Label>
          <NeoInput value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme S.L." data-testid="profile-name" />
        </div>
        <div>
          <Label className="eyebrow text-xs">CIF / NIF</Label>
          <NeoInput value={form.cif || ""} onChange={(e) => setForm({ ...form, cif: e.target.value })} placeholder="B12345678" data-testid="profile-cif" />
        </div>
        <div className="sm:col-span-2">
          <Label className="eyebrow text-xs">Domicilio</Label>
          <NeoInput value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="C/ Mayor 1, 28001 Madrid" data-testid="profile-address" />
        </div>
        <div>
          <Label className="eyebrow text-xs">Email</Label>
          <NeoInput type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="hola@empresa.com" data-testid="profile-email" />
        </div>
        <div>
          <Label className="eyebrow text-xs">Teléfono</Label>
          <NeoInput value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+34 911 234 567" data-testid="profile-phone" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-5">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-none border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] hover:bg-black font-black shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917]"
          data-testid="btn-save-profile"
        >
          {saving ? "Guardando…" : "Guardar datos"}
        </Button>
        <label
          className={`inline-flex items-center gap-2 h-10 px-4 border-2 border-[#1C1917] bg-[#FFC53D] hover:bg-[#FFD86F] font-black text-xs tracking-wider uppercase cursor-pointer shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917] transition-all ${logoUploading ? "opacity-60" : ""}`}
          data-testid="btn-upload-logo"
        >
          {logoUploading ? "Subiendo…" : profile?.logo_file_id ? "Reemplazar logo" : "Subir logo"}
          <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={logoUploading} />
        </label>
        {profile?.logo_file_id && (
          <Button
            variant="ghost"
            onClick={removeLogo}
            className="rounded-none text-[#E5484D] font-bold hover:bg-[#FCE7E8]"
            data-testid="btn-remove-logo"
          >
            Quitar logo
          </Button>
        )}
      </div>
    </NeoCard>
  );
}

export default function DocGen() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("prompt"); // "prompt" | "templates" | "wizards"
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [wizards, setWizards] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [search, setSearch] = useState("");
  const [openTpl, setOpenTpl] = useState(null);
  const [varForm, setVarForm] = useState({});
  const [instruction, setInstruction] = useState("");
  const [outputFormat, setOutputFormat] = useState("both");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    api.get("/docgen/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    api.get("/docgen/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    api.get("/docgen/wizards").then((r) => setWizards(r.data.wizards || [])).catch(() => {});
    api.get("/docgen/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  const filteredTpls = useMemo(() => {
    let list = templates;
    if (activeCat) list = list.filter((t) => t.category === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((t) => t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
    }
    return list;
  }, [templates, activeCat, search]);

  async function openTemplate(t) {
    try {
      const { data } = await api.get(`/docgen/templates/${t.id}`);
      setOpenTpl(data);
      setVarForm({});
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function generate() {
    setGenerating(true);
    setResult(null);
    try {
      let payload;
      if (tab === "prompt") {
        if (instruction.trim().length < 10) {
          toast.error("Describe lo que necesitas con más detalle");
          setGenerating(false);
          return;
        }
        payload = { mode: "prompt", instruction, output_format: outputFormat };
      } else {
        if (!openTpl) {
          toast.error("Selecciona una plantilla");
          setGenerating(false);
          return;
        }
        payload = {
          mode: "template",
          template_id: openTpl.id,
          variables: varForm,
          output_format: outputFormat,
        };
      }
      const { data } = await api.post("/docgen/generate", payload);
      setResult(data);
      toast.success("Documento generado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  }

  function goSign(file) {
    // Pre-load PdfSign with the generated PDF.
    navigate(`/tools/pdf/sign?file=${file.id}`);
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]" data-testid="docgen-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="eyebrow text-xs">AI Studio</span>
            <h1 className="hero-display text-4xl sm:text-5xl text-[#1C1917] mt-1">Generador de documentos</h1>
            <p className="text-sm text-[#57534E] mt-2 max-w-2xl">
              Pídele a la IA un documento empresarial en lenguaje natural, o elige entre +100 plantillas legales (España).
            </p>
          </div>
          <Link
            to="/tools/pdf/sign"
            className="hidden md:inline-flex items-center text-xs font-black tracking-wider uppercase text-[#57534E] hover:text-[#1C1917]"
          >
            ¿Firmar un PDF? →
          </Link>
        </div>

        {/* Tabs */}
        <div className="mt-8 inline-flex border-2 border-[#1C1917] shadow-[3px_3px_0_0_#1C1917]" data-testid="docgen-tabs">
          <button
            onClick={() => { setTab("prompt"); setResult(null); }}
            className={`px-5 h-11 font-black text-xs tracking-wider uppercase transition-colors ${
              tab === "prompt" ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
            }`}
            data-testid="tab-prompt"
          >
            IA libre
          </button>
          <button
            onClick={() => { setTab("templates"); setResult(null); }}
            className={`px-5 h-11 font-black text-xs tracking-wider uppercase border-l-2 border-[#1C1917] transition-colors ${
              tab === "templates" ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
            }`}
            data-testid="tab-templates"
          >
            +100 plantillas
          </button>
          <button
            onClick={() => { setTab("wizards"); setResult(null); }}
            className={`px-5 h-11 font-black text-xs tracking-wider uppercase border-l-2 border-[#1C1917] transition-colors ${
              tab === "wizards" ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
            }`}
            data-testid="tab-wizards"
          >
            Asistente guiado
          </button>
        </div>

        {/* Profile toggle */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowProfile((v) => !v)}
            className="text-xs font-black tracking-wider uppercase text-[#1C1917] hover:underline"
            data-testid="btn-toggle-profile"
          >
            {showProfile ? "↑ Ocultar" : "↓ Configurar"} membrete y datos de empresa
          </button>
          {profile?.name && !showProfile && (
            <span className="text-xs text-[#57534E]">
              Membrete: <strong className="text-[#1C1917]">{profile.name}</strong>
              {profile.logo_file_id ? " · con logo" : ""}
            </span>
          )}
        </div>
        {showProfile && (
          <div className="mt-4">
            <CompanyProfileEditor profile={profile} onSaved={setProfile} />
          </div>
        )}

        {/* Body */}
        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Left: input area */}
          <div className="lg:col-span-2">
            {tab === "wizards" ? (
              <div data-testid="wizards-grid">
                <div className="mb-4">
                  <h2 className="display text-2xl text-[#1C1917]">Contratos paso a paso</h2>
                  <p className="text-sm text-[#57534E] mt-1 max-w-xl">
                    Te hacemos las preguntas correctas. Las cláusulas se incluyen <strong>solo si las necesitas</strong>.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {wizards.map((w) => (
                    <Link
                      key={w.id}
                      to={`/tools/docgen/wizard/${w.id}`}
                      className="block border-2 border-[#1C1917] bg-white p-5 shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
                      data-testid={`wiz-card-${w.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="w-10 h-10 grid place-items-center bg-[#7C3AED] border-2 border-[#1C1917] text-white font-black text-xs">
                          GUI
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#57534E]">
                          {w.steps_count} pasos · {w.estimated_minutes} min
                        </span>
                      </div>
                      <h3 className="mt-4 font-black text-[#1C1917]">{w.title}</h3>
                      <p className="text-xs text-[#57534E] mt-2 leading-relaxed">{w.description}</p>
                      <div className="mt-4 flex items-center gap-2 text-xs font-black tracking-wider uppercase text-[#7C3AED]">
                        Empezar →
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : tab === "prompt" ? (
              <NeoCard className="p-6" data-testid="prompt-panel">
                <Label className="eyebrow text-xs">¿Qué documento quieres?</Label>
                <NeoTextarea
                  rows={6}
                  className="mt-2"
                  placeholder='Ej: "Hazme un despido objetivo para la trabajadora María García porque se acabó el servicio donde ella trabajaba"'
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  data-testid="prompt-instruction"
                />
                <div className="mt-5">
                  <Label className="eyebrow text-xs">Formato de salida</Label>
                  <div className="mt-2"><FormatPicker value={outputFormat} onChange={setOutputFormat} /></div>
                </div>
                <Button
                  onClick={generate}
                  disabled={generating}
                  className="mt-6 w-full h-12 rounded-none border-2 border-[#1C1917] bg-[#7C3AED] text-white hover:bg-[#6D28D9] font-black tracking-wider shadow-[4px_4px_0_0_#1C1917] hover:shadow-[6px_6px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
                  data-testid="btn-generate-prompt"
                >
                  {generating ? "Generando con IA…" : "Generar documento"}
                </Button>
              </NeoCard>
            ) : openTpl ? (
              <NeoCard className="p-6" data-testid="template-form-panel">
                <button
                  onClick={() => { setOpenTpl(null); setVarForm({}); }}
                  className="text-xs font-black tracking-wider uppercase text-[#57534E] hover:text-[#1C1917] mb-4"
                  data-testid="btn-back-templates"
                >
                  ← Volver a plantillas
                </button>
                <span className="eyebrow text-xs">{categories.find((c) => c.id === openTpl.category)?.name || openTpl.category}</span>
                <h2 className="display text-2xl mt-1 text-[#1C1917]">{openTpl.title}</h2>
                <p className="text-sm text-[#57534E] mt-2">{openTpl.description}</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {openTpl.variables.map((v) => (
                    <div key={v.key} className={v.type === "textarea" ? "sm:col-span-2" : ""}>
                      <Label className="eyebrow text-xs">
                        {v.label}{v.required ? " *" : ""}
                      </Label>
                      <div className="mt-1">
                        <VariableField v={v} value={varForm[v.key]} onChange={(k, val) => setVarForm({ ...varForm, [k]: val })} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <Label className="eyebrow text-xs">Formato de salida</Label>
                  <div className="mt-2"><FormatPicker value={outputFormat} onChange={setOutputFormat} /></div>
                </div>
                <Button
                  onClick={generate}
                  disabled={generating}
                  className="mt-6 w-full h-12 rounded-none border-2 border-[#1C1917] bg-[#7C3AED] text-white hover:bg-[#6D28D9] font-black tracking-wider shadow-[4px_4px_0_0_#1C1917] hover:shadow-[6px_6px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
                  data-testid="btn-generate-template"
                >
                  {generating ? "Generando con IA…" : `Generar ${openTpl.title.toLowerCase()}`}
                </Button>
              </NeoCard>
            ) : (
              <div data-testid="templates-grid">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <NeoInput
                    placeholder="Buscar plantilla…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="templates-search"
                  />
                  <span className="text-xs text-[#57534E] uppercase tracking-wider font-bold">
                    {filteredTpls.length} de {templates.length} plantillas
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                  <button
                    onClick={() => setActiveCat(null)}
                    className={`px-3 h-9 border-2 border-[#1C1917] text-xs font-black tracking-wider uppercase transition-colors ${
                      activeCat === null ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
                    }`}
                    data-testid="cat-all"
                  >
                    Todas
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setActiveCat(c.id)}
                      className={`px-3 h-9 border-2 border-[#1C1917] text-xs font-black tracking-wider uppercase transition-colors ${
                        activeCat === c.id ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
                      }`}
                      data-testid={`cat-${c.id}`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {filteredTpls.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => openTemplate(t)}
                      className="text-left border-2 border-[#1C1917] bg-white p-4 shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
                      data-testid={`tpl-${t.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div
                          className="w-2 h-12 flex-shrink-0"
                          style={{ background: CATEGORY_COLORS[t.category] || "#1C1917" }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-black text-sm text-[#1C1917] leading-tight">{t.title}</div>
                          <div className="text-xs text-[#57534E] mt-1 line-clamp-2">{t.description}</div>
                          <div className="text-[10px] uppercase tracking-wider text-[#57534E] font-bold mt-2">
                            {t.variables_count} campos
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                  {filteredTpls.length === 0 && (
                    <div className="sm:col-span-2 text-center py-12 text-[#57534E] text-sm">
                      Ninguna plantilla coincide con tu búsqueda.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right: tips / result */}
          <div className="lg:col-span-1">
            <NeoCard className="p-6 bg-[#FFE5A0]" data-testid="docgen-tips">
              <span className="eyebrow text-xs">Cómo funciona</span>
              <ol className="mt-3 space-y-3 text-sm text-[#1C1917] list-decimal list-inside">
                <li>Configura los datos de tu empresa <strong>(arriba)</strong> para añadir el membrete.</li>
                <li>Describe el documento o elige una plantilla.</li>
                <li>La IA genera contenido legal y profesional <strong>en segundos</strong>.</li>
                <li>Descarga en <strong>PDF / Word</strong> o fírmalo directamente.</li>
              </ol>
              <div className="mt-5 border-t-2 border-[#1C1917] pt-4">
                <p className="text-xs text-[#1C1917] font-bold">⚠ Aviso legal</p>
                <p className="text-xs text-[#57534E] mt-1 leading-relaxed">
                  Los documentos generados son borradores profesionales. Te recomendamos una revisión final por tu asesor legal.
                </p>
              </div>
            </NeoCard>
          </div>
        </div>

        {/* Result */}
        <ResultBox result={result} onSign={goSign} />
      </div>
    </div>
  );
}
